import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { type EntityKey, entityResource, entityRows, entityValue } from '../lib/entities';

export interface EntityOption {
  value: string;
  label: string;
  hint?: string;
  /** The heading this option sits under — the room a storage area is in, say. */
  group?: string;
}

export interface EntityIndex {
  options: EntityOption[];
  /**
   * id → name, qualified by its group wherever it has one.
   *
   * "Shelf" is the name of a place in all three rooms, so a movements row showing it bare leaves
   * the reader unable to say which shelf lost a bottle. Qualified it reads "The Bodega — Shelf",
   * which is what somebody would have said out loud.
   */
  label: (id: unknown) => string;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Loads a referenceable collection once and hands back both the picker options and an id → name
 * lookup. Cached under the same key the list tables use, so a collection already on screen costs
 * nothing extra to resolve, and creating a record refreshes every picker that can offer it.
 */
export function useEntityIndex(key: EntityKey | undefined): EntityIndex {
  const resource = key ? entityResource(key) : undefined;

  const query = useQuery({
    queryKey: listKey(resource?.listPath ?? 'none'),
    queryFn: () => rawApi<unknown>('GET', resource!.listPath),
    enabled: Boolean(resource),
    // Reference data changes rarely, and a picker that refetches on every focus feels twitchy.
    staleTime: 60_000,
  });

  const rows = key ? entityRows(key, query.data) : [];

  const options: EntityOption[] = key
    ? rows.map((row) => ({
        value: entityValue(key, row),
        label: resource!.label(row) || '(unnamed)',
        hint: resource!.hint?.(row),
        group: resource!.group?.(row),
      }))
    : [];

  const byId = new Map(options.map((o) => [o.value, o.group ? `${o.group} — ${o.label}` : o.label]));

  return {
    options,
    label: (id: unknown) => {
      if (id === null || id === undefined || id === '') return '—';
      const found = byId.get(String(id));
      if (found) return found;
      // Distinguish "still loading" from "this points at something that no longer exists" —
      // the second is a data problem the owner may need to know about.
      return query.isLoading ? '…' : 'Not found';
    },
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
